import './style.modules.scss'

import { MaterialIcon, MaterialIconSize } from '@ampcore/adel-react'
import { AmpcoreMaterialIconId } from '@ampcore/ampcom-contracts'
import React, { useEffect, useState } from 'react'

import { getBrowser } from '../../lib/DeviceDetection/getBrowser'
import { getOperatingSystem } from '../../lib/DeviceDetection/getOperatingSystem'
import { Browser } from '../../types/Browser.type'
import { Devices } from '../../types/Devices.type'
import { OperatingSystem } from '../../types/OperatingSystem.type'
import { AppStoreVariant } from './AppStoreVariant.type'

declare global {
  interface Window {
    MSStream: string
  }
}

export type AppStoreBannerProps = {
  isAndroidEnabled?: boolean
  isIosAllDevicesEnabled?: boolean
  variant: AppStoreVariant
  testId?: string
}

/**
 * iOS safari banner is based a page meta tag, this handles all other scenarios
 */
export const AppStoreBanner = (props: AppStoreBannerProps) => {
  const { isAndroidEnabled, isIosAllDevicesEnabled, variant, testId } = props
  const [device, setDevice] = useState<Devices | null>(null)
  const [isRecentlyClosed, setIsRecentlyClosed] = useState<boolean | null>(null)
  const [open, setOpen] = useState(true)
  const storageKey = 'app-store-banner-closed-timestamp'
  const browser = getBrowser()
  const operatingSystem = getOperatingSystem()
  const iosLogo = `${window.AMPCOMPATH}images/logos/amp-ios-icon.png`
  const appleAppStoreUrl =
    'https://apps.apple.com/app/apple-store/id437992173?pt=458199%26ct=smart-banner-ns%26mt=8'
  const googlePlayStoreUrl =
    'https://play.google.com/store/apps/details?id=com.ameriprise.AmeripriseFinancial&hl=en-US'
  const supportedDevices: Devices[] = [
    Devices.IOS_NON_SAFARI_BROWSER,
    Devices.ANDROID
  ]

  const closeBanner = () => {
    localStorage.setItem(storageKey, Date.now().toString())
    setOpen(false)
  }

  // categorize the device based on the variant
  useEffect(() => {
    if (variant === AppStoreVariant.ANDROID) {
      setDevice(Devices.ANDROID)
      return
    }

    if (variant === AppStoreVariant.IOS_NON_SAFARI_BROWSER) {
      setDevice(Devices.IOS_NON_SAFARI_BROWSER)
      return
    }

    if (variant === AppStoreVariant.DETECT_DEVICE) {
      if (operatingSystem === OperatingSystem.IOS) {
        if (browser === Browser.SAFARI) {
          setDevice(Devices.IOS_SAFARI_BROWSER)
          return
        }

        setDevice(Devices.IOS_NON_SAFARI_BROWSER)
        return
      }

      if (operatingSystem === OperatingSystem.ANDROID) {
        setDevice(Devices.ANDROID)
      }
    }
  }, [browser, operatingSystem, variant])

  // check if the banner should be shown based on the timeframe
  const storedTimestamp = localStorage.getItem(storageKey)

  useEffect(() => {
    const closedAtTimestamp = Number(storedTimestamp)

    if (closedAtTimestamp) {
      const now = Date.now() // epoch timestamp
      const retentionPeriodInMilliseconds = 6 * 30 * 24 * 60 * 60 * 1000 // 6 months in milliseconds
      const retentionThresholdTimestamp = now - retentionPeriodInMilliseconds

      // if closed button was clicked more than the retention threshold ago, prompt user again
      if (closedAtTimestamp > retentionThresholdTimestamp) {
        setIsRecentlyClosed(true)
        return
      }
    }

    setIsRecentlyClosed(false)
  }, [device, storedTimestamp])

  const handleButtonClick = () => {
    if (!device) {
      console.error('Device not set')
      return
    }

    if (device === Devices.ANDROID) {
      window.open(googlePlayStoreUrl, '_blank', 'noopener')
    }

    if (device === Devices.IOS_NON_SAFARI_BROWSER) {
      window.open(appleAppStoreUrl, '_blank', 'noopener')
    }
  }

  // only show banner if open within time frame and for supported devices
  // safari banner is driven by meta tag, not this component
  if (
    isRecentlyClosed ||
    !open ||
    !device ||
    !supportedDevices.includes(device)
  ) {
    return null
  }

  // TODO: remove feature flag for Android
  if (!isAndroidEnabled && device === Devices.ANDROID) {
    return null
  }

  // TODO: remove feature flag for iOS
  if (!isIosAllDevicesEnabled && browser !== Browser.CHROME) {
    return null
  }

  return (
    <div className="AppStoreBanner" id="ios-mobile-banner">
      <div className="AppStoreBanner-grid">
        <div className="AppStoreBanner-gridColumn x">
          <button
            data-testid={testId ? `${testId}-close-btn` : null}
            className="AppStoreBanner-close"
            data-analytics="mobile-app-banner-dismiss-button"
            onClick={() => closeBanner()}
          >
            <MaterialIcon
              icon={AmpcoreMaterialIconId.CLOSE}
              size={MaterialIconSize.SIZE_18}
              className="AppStoreBanner--closeButton"
              ariaLabel="Close Banner"
            />
          </button>
        </div>
        <div className="AppStoreBanner-gridColumn">
          <img
            className="AppStoreBanner-image"
            src={iosLogo}
            alt="Ameriprise mobile app link"
            width="64px"
            height="64px"
          />
        </div>
        <div
          className="AppStoreBanner-gridColumn AppStoreBanner-text"
          data-testid={testId ? `${testId}-text` : null}
        >
          <p>
            Ameriprise Financial <br />
            {device == Devices.ANDROID ? 'GET - On the Play Store' : ''}
            {device == Devices.IOS_NON_SAFARI_BROWSER
              ? 'GET - On the App Store'
              : ''}
          </p>
        </div>
        <div className="AppStoreBanner-gridColumn">
          <button
            onClick={handleButtonClick}
            data-analytics="mobile-app-banner-open-button"
            data-testid={testId ? `${testId}-open` : null}
          >
            Open
          </button>
        </div>
      </div>
    </div>
  )
}
